import { createContext, useContext, useEffect, useState } from 'react';
import {
  getAuth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged
} from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import * as Sentry from '@sentry/react';
import { app } from '../firebase/config';
import { db } from '../firebase/config';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser]                     = useState(null);
  const [profile, setProfile]               = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [loading, setLoading]               = useState(true);
  const auth = getAuth(app);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser);
      setLoading(false);

      if (!firebaseUser) {
        setProfile(null);
        setProfileLoading(false);
        // Clear Sentry user so logged-out errors aren't attributed to anyone
        Sentry.setUser(null);
        return;
      }

      // Tag all Sentry events with this user's identity
      Sentry.setUser({ id: firebaseUser.uid, email: firebaseUser.email });

      // Load user profile from Firestore (non-blocking)
      setProfileLoading(true);
      getDoc(doc(db, 'users', firebaseUser.uid))
        .then((snap) => {
          setProfile(snap.exists() ? snap.data() : null);
        })
        .catch(() => {
          setProfile(null);
        })
        .finally(() => {
          setProfileLoading(false);
        });
    });

    return unsubscribe;
  }, [auth]);

  const login = async (email, password) => {
    return signInWithEmailAndPassword(auth, email, password);
  };

  const signup = async (email, password, consentTimestamp) => {
    const credential = await createUserWithEmailAndPassword(auth, email, password);
    // Write consent record immediately after account creation.
    // Both timestamps use the same value (the moment the form was submitted).
    // These fields are write-once — the Firestore rule prevents any future update
    // from overwriting them, preserving the audit trail for GDPR compliance.
    await setDoc(doc(db, 'users', credential.user.uid), {
      email: credential.user.email,
      tosConsentAt: consentTimestamp,
      privacyConsentAt: consentTimestamp,
      createdAt: consentTimestamp,
    });
    return credential;
  };

  const logout = async () => {
    return signOut(auth);
  };

  /** Save / update the user's profile in Firestore and local state. */
  const saveProfile = async (data) => {
    if (!user) return;
    await setDoc(doc(db, 'users', user.uid), data, { merge: true });
    setProfile((prev) => ({ ...prev, ...data }));
  };

  const value = {
    user,
    profile,
    profileLoading,
    saveProfile,
    login,
    signup,
    logout,
    loading,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
